'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useRouter } from '@/i18n/navigation';
import { Download, ExternalLink, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type BrowserType = 'chrome' | 'firefox' | 'safari' | 'other';

function detectBrowser(): BrowserType {
  if (typeof window === 'undefined') return 'other';

  const userAgent = navigator.userAgent.toLowerCase();

  // Check Safari first (Safari doesn't have 'chrome' in UA but has 'safari')
  if (
    userAgent.includes('safari') &&
    !userAgent.includes('chrome') &&
    !userAgent.includes('chromium')
  ) {
    return 'safari';
  }

  // Check Firefox
  if (userAgent.includes('firefox')) {
    return 'firefox';
  }

  // Chrome, Edge, Opera, and other Chromium-based browsers
  if (userAgent.includes('chrome') || userAgent.includes('chromium')) {
    return 'chrome';
  }

  return 'other';
}

export function PWAInstallPrompt() {
  const t = useTranslations();
  const router = useRouter();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [browser, setBrowser] = useState<BrowserType>('other');

  useEffect(() => {
    // Detect browser on mount
    setBrowser(detectBrowser());

    // Check if user previously dismissed
    const isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
    const isInstalled = localStorage.getItem('pwa-installed') === 'true';

    if (isDismissed || isInstalled) {
      return;
    }

    const detectedBrowser = detectBrowser();

    // For Firefox/Safari, show instructions prompt after delay
    if (detectedBrowser === 'firefox' || detectedBrowser === 'safari') {
      setTimeout(() => {
        setShowPrompt(true);
      }, 10000);
      return;
    }

    // Listen for beforeinstallprompt event (Chromium browsers only)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);

      // Show prompt after 10 seconds (non-intrusive)
      setTimeout(() => {
        setShowPrompt(true);
      }, 10000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    const handleAppInstalled = () => {
      localStorage.setItem('pwa-installed', 'true');
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      localStorage.setItem('pwa-installed', 'true');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleInstructionsClick = () => {
    router.push('/install-app');
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  const isUnsupportedBrowser = browser === 'firefox' || browser === 'safari';

  return (
    <div className="fixed bottom-20 right-4 z-50 max-w-sm sm:bottom-4">
      <Card className="border-primary/50 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Download className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-2">{t('pwaInstallTitle')}</p>
              <p className="text-xs text-muted-foreground mb-3">{t('pwaInstallDescription')}</p>
              <div className="flex gap-2">
                {isUnsupportedBrowser ? (
                  <Button size="sm" onClick={handleInstructionsClick} className="flex-1 gap-1">
                    <ExternalLink className="h-3 w-3" />
                    {t('pwaInstallInstructions')}
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleInstallClick} className="flex-1">
                    {t('pwaInstallButton')}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
